<template>
  <ReportLayout @period-changed="onPeriodChanged" :back-page="{ name: 'CenterReports', params: { centerId, id: srId } }">
    <div class="my-3">
      <Transition name="grow">
        <div v-if="showFamilies" class="flex flex-wrap">
          <FamilyBadge @click="badge.visible = !badge.visible" class="m-1"
            v-for="badge in centerReportStore.familiesSortedByCurrentTotal"
            :label="badge.label" :color="badge.color" :active="badge.visible" />
        </div>
      </Transition>
    </div>

    <div class="grid gap-5">
      <!-- Analyse des familles -->
      <ReportCard v-if="productsReport" :title="$t('report.families_view')">
        <FamiliesInsights :products-report="productsReport"
          :center="center" :report-id="srId"
          @navigate-to-family="navigateToFamily" />
      </ReportCard>

      <ReportCard :title="$t('report.sales_distribution.distribution_title')">

        <Transition name="fade" mode="out-in">
          <div v-if="loaded">
            <div v-if="!centerReportStore.showingProducts">
              <FamiliesDoughnutChart
                v-if="productsReport"
                :products-report="productsReport"
                :center-id="centerId"
              />
              <div v-else class="flex h-32 justify-center items-center">
                <p class="text-muted-foreground">{{
                  $t("report.sales_distribution.no_data") }}
                </p>
              </div>
            </div>

            <div v-else>
              <div class="mb-4 flex items-center">
                <button @click="centerReportStore.showFamilies()"
                  class="text-blue-600 hover:text-blue-800 flex items-center">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  {{ $t('common.back') }}
                </button>
                <h3 class="ml-4 text-lg font-medium">{{
                  centerReportStore.selectedFamily }}</h3>
              </div>

              <Transition name="fade" mode="out-in">
                <div v-if="centerReportStore.loadingProducts" key="loading"
                  class="flex h-32 justify-center items-center">
                  <Loading />
                </div>

                <div
                  v-else-if="centerReportStore.productsDoughnutData.datasets[0]?.data?.length > 0"
                  key="data">
                  <!-- Products chart implementation would go here -->
                  <div class="flex h-32 justify-center items-center">
                    <p class="text-muted-foreground">Products chart (to be implemented)</p>
                  </div>
                </div>

                <div v-else key="no-data"
                  class="flex h-32 justify-center items-center">
                  <p class="text-muted-foreground">{{
                    $t("report.sales_distribution.no_data") }}</p>
                </div>
              </Transition>
            </div>
          </div>
          <Loading v-else />
        </Transition>
      </ReportCard>
    </div>
  </ReportLayout>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useRouter } from 'vue-router'
import { useCenterReportStore } from '../../../stores/centerReportStore'
import { useSalesReportsStore } from '../../../stores/salesReports'
import { useAppSettingsStore } from '../../../stores/AppSettingsStore'
import ReportCard from '../../../components/ReportCard.vue'
import Loading from '../../../components/Loading.vue'
import FamilyBadge from '../../../components/FamilyBadge.vue'
import ReportLayout from '../../../layouts/ReportLayout.vue'
import FamiliesInsights from '../../../components/FamiliesInsights.vue'
import FamiliesDoughnutChart from '../../../components/report-sections/FamiliesDoughnutChart.vue'

const router = useRouter();

const centerReportStore = useCenterReportStore();
const salesReportsStore = useSalesReportsStore();
const appSettingsStore = useAppSettingsStore();

const centerId = parseInt(router.currentRoute.value.params.centerId as string);
const srId = router.currentRoute.value.params.id as string;

const card = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(centerId, srId);
})

const loaded = ref(false);
const showFamilies = ref(false);

const center = computed(() => {
  let c = appSettingsStore.getCenter(centerId);
  if (!c) {
    throw new Error("[CenterFamiliesReport] Center not loaded. Should not be here")
  }

  return c;
})

const productsReport = computed(() => {
  return card.value?.productsReport || null;
})

const navigateToFamily = (familyName: string) => {
  router.push({
    name: 'family-detail-report',
    params: {
      centerId,
      id: srId,
      familyName,
    },
  })
}

const onPeriodChanged = async () => {
  if (!card.value) return
  card.value.productsReport = null

  try {
    await card.value.fetchProductsReport()
  } catch {
    // errors handled inside fetch methods
  }
}

onMounted(async () => {
  loaded.value = true;
})
</script>
